import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SigeProject, Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock, aufloesePikto, PLAN_SCHRIFT, planSchriftPt } from './types';
import { usePdfExport } from './hooks/usePdfExport';
import { erzeugeSigePlan } from '../../services/pdf/generatorSigePlan';
import { ladePiktogrammBilder, piktoKeysFuer, pruefePiktoQuelle } from './piktogrammBilder';
import { ladeEinweisungQrFuerProjekt } from '../../services/sigeEinweisungService';
import type { SigePlanFusszeile } from '../../services/pdf/generatorSigePlan';
import { X, Download, Loader2, Printer, FolderUp, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../Button';
import { istNativeApp } from '../../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../../services/dateiAusgabe';
import { blobFuerUrl } from '../../services/nativeShims';
import { NativePdfSeiten } from '../NativePdfSeiten';
import { reiheSchriftAuftrag } from './schriftWarteschlange';
import { projektFuerDruck, useSigePlanVorlagen, type SigePlanVorlagen } from '../../services/sigePlanVorlagen';
import { PlotterDialog } from './PlotterDialog';
import { meldePlotAngekommen, merkePlotAuftrag } from './plotAbrechnungen';
import { plotDokumentSigePlan } from '../../services/plotter/plotStatus';
import {
  sigePlanAblageKnopfText, sigePlanDateiname, sigePlanFassungRef, sigePlanInhaltsStand,
  type Blattmass, type SigePlanAblageStand,
} from './planAblage';
import { ablageStandText, ablageStandTon, type AblageTon } from '../../services/ablageHilfen';

/** Farbe der Ablage-Statuszeile je Ton — Warnung (verwaiste Datei) ist kein Grün. */
const ABLAGE_TON_KLASSE: Record<AblageTon, string> = {
  laeuft: 'text-slate-500',
  erfolg: 'text-emerald-700',
  warnung: 'text-amber-700',
  fehler: 'text-red-700',
};

/** Der angezeigte Plan, so wie er abgelegt würde. */
export interface PlanStandFuerAblage {
  blob: Blob;
  /** Der Plan wie gedruckt (`projektFuerDruck`): Breite und Schrift aufgelöst. */
  druck: SigeProject;
  fassungRef: string;
  blatt: Blattmass;
}

interface PlanVorschauProps {
  sigeProject: SigeProject;
  arbeitsschritte: Arbeitsschritt[];
  gefaehrdungen: Record<string, Gefaehrdung[]>;
  massnahmen: Record<string, Schutzmassnahme[]>;
  bloecke: SigeBlock[];
  /** Projekt-Nr. und -bezeichnung des Hauptprojekts fuer die Fusszeile des Plans. */
  fusszeile?: SigePlanFusszeile;
  onDownload?: () => void; // übersteuert Standard-Download (z.B. für Abrechnungs-Flow)
  /**
   * Speichert eine neue Grundschrift (`darstellung.schriftPt`). Ohne Rückruf
   * — Nur-Ansicht, Assistent — erscheint kein Regler: ein Regler, der nur die
   * Vorschau ändert, aber nicht den Download, würde in die Irre führen.
   * Scheitert das Speichern, muss das Promise verwerfen — der Regler springt
   * dann zurück und meldet es.
   */
  onSchriftAendern?: (pt: number) => void | Promise<void>;
  /**
   * Legt den ANGEZEIGTEN Plan in der zentralen Ablage ab (Stufe 1A). Ohne
   * Rückruf — Assistent — erscheint kein Knopf. Mitgegeben werden dieselben
   * Bytes wie in der Vorschau, der Plan wie gedruckt, die Kennung des
   * Inhaltsstands und das Blattmaß; den Anlass entscheidet der Editor.
   */
  onAblegen?: (stand: PlanStandFuerAblage) => void;
  /**
   * Ergebnis des letzten Ablageversuchs. Der Editor hält es, weil auch der
   * Export mit Leistungsbuchung ablegt; gezeigt wird es nur, solange die
   * Vorschau denselben Stand zeigt (`fassungRef`).
   */
  ablage?: SigePlanAblageStand | null;
  /**
   * „Erneut ablegen" nach einem Fehlschlag: wiederholt den FESTGEHALTENEN
   * Auftrag — dieselben Bytes, dieselbe Kennung, derselbe Anlass (auch ein
   * Versand bleibt Versand). Fehlt der Rückruf, fällt der Knopf auf
   * `onAblegen` zurück.
   */
  onErneutAblegen?: (stand: PlanStandFuerAblage) => void;
  /**
   * Projekt (`projects.id`), in dem ein neuer Plot-Auftrag abgerechnet wird:
   * die Vorschau merkt ihn samt angezeigtem Stand (plotAbrechnungen.ts), der
   * Editor des Projekts öffnet die Abrechnung, sobald er angekommen ist. Ohne
   * — Assistent — bucht Plotten nichts.
   */
  plotAbrechnungProjektId?: string;
  onClose: () => void;
}

const fmtZahl = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 1 });

type Blatt = { breiteMm: number; hoeheMm: number; gekappt: boolean };

const PlanVorschauFenster: React.FC<PlanVorschauProps & { vorlagen: SigePlanVorlagen }> = ({
  sigeProject, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, fusszeile, onDownload, onSchriftAendern,
  onAblegen, ablage, onErneutAblegen, plotAbrechnungProjektId, onClose, vorlagen
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blatt, setBlatt] = useState<Blatt | null>(null);
  // Der Plan selbst für „An Plotter senden“ und „In Dokumente ablegen“ —
  // dieselbe Datei wie in der Vorschau, samt Kennung ihres Inhaltsstands.
  const [planStand, setPlanStand] = useState<PlanStandFuerAblage | null>(null);
  // Beim Öffnen festgehalten: erzeugt ein Hintergrundabgleich den Plan neu,
  // bleibt der Dialog (mit seiner Auftrags-ID) beim gezeigten Stand.
  const [plotter, setPlotter] = useState<{ stand: PlanStandFuerAblage; blatt: Blatt } | null>(null);
  const [diag, setDiag] = useState('');
  const { exportPdf } = usePdfExport();
  // Schriftregler: lokal sofort, gespeichert erst nach kurzer Ruhe. Jeder
  // gespeicherte Wert erzeugt das PDF neu (über `sigeProject`) — beim Ziehen
  // entstünde sonst für jeden Zwischenwert ein Plan und ein Schreibvorgang.
  // Ohne eigenen Wert im Plan steht er auf der Schriftvorgabe der Firma.
  const gespeichertPt = planSchriftPt(sigeProject.darstellung, vorlagen.standardSchriftPt);
  const [schriftPt, setSchriftPt] = useState(gespeichertPt);
  // Der Reglerstand SOFORT beim Ziehen, nicht erst nach dem Rendern: ein
  // scheiternder älterer Auftrag fragt ihn, bevor er zurückdreht (Codex-Review
  // 04.10.2026 — in der 400-ms-Ruhezeit ist der neue Wert noch nicht eingereiht).
  const reglerRef = useRef(gespeichertPt);
  const [schriftFehler, setSchriftFehler] = useState('');
  // Der Editor reicht bei jedem Render eine neue Funktion; als Abhängigkeit
  // des Entprell-Effekts setzte sie den Timer jedes Mal zurück.
  const speichernRef = useRef(onSchriftAendern);
  useEffect(() => { speichernRef.current = onSchriftAendern; });
  // Noch nicht gespeicherter Wert (für das Schließen innerhalb der Ruhezeit).
  const ausstehendRef = useRef<{ pt: number; vorher: number } | null>(null);
  // Die Warteschlange hängt am PROJEKT, nicht an diesem Fenster: ein Auftrag
  // aus einer geschlossenen Vorschau darf den Wert einer neu geöffneten nicht
  // überschreiben (schriftWarteschlange.ts).
  const projektKeyRef = useRef(sigeProject.id);
  useEffect(() => { projektKeyRef.current = sigeProject.id; });
  const speichere = useCallback((pt: number, vorher: number) => {
    ausstehendRef.current = null;
    void reiheSchriftAuftrag(projektKeyRef.current, {
      schreibe: async () => {
        const f = speichernRef.current;
        if (f) await f(pt);
      },
      beiErfolg: () => setSchriftFehler(''),
      beiFehler: (e) => {
        setSchriftFehler(`Schriftgröße nicht gespeichert: ${e instanceof Error ? e.message : String(e)}`);
        // Inzwischen anders eingestellt (in der Ruhezeit oder schon eingereiht):
        // nicht zurückdrehen — der neuere Wert wird gleich selbst gespeichert
        // und räumt die Meldung bei Erfolg wieder ab.
        if (reglerRef.current !== pt) return;
        // Der Editor hat seinen Stand schon zurückgenommen; der Regler folgt,
        // sonst versuchte der Effekt es alle 400 ms erneut.
        reglerRef.current = vorher;
        setSchriftPt(vorher);
      },
    });
  }, []);
  useEffect(() => {
    if (schriftPt === gespeichertPt) { ausstehendRef.current = null; return; }
    const auftrag = { pt: schriftPt, vorher: gespeichertPt };
    ausstehendRef.current = auftrag;
    const t = setTimeout(() => speichere(auftrag.pt, auftrag.vorher), 400);
    return () => clearTimeout(t);
  }, [schriftPt, gespeichertPt, speichere]);
  // Schließen innerhalb der Ruhezeit: den eingestellten Wert nicht verwerfen.
  useEffect(() => {
    const ausstehend = ausstehendRef;
    return () => {
      const auftrag = ausstehend.current;
      if (auftrag) speichere(auftrag.pt, auftrag.vorher);
    };
  }, [speichere]);
  // Android-App: kein PDF-Betrachter in der WebView — Seiten per pdf.js
  // (NativePdfSeiten), Ausgabe über den Teilen-Dialog. Browser: unverändert.
  const nativ = istNativeApp();
  const [teilenLaeuft, setTeilenLaeuft] = useState(false);

  useEffect(() => {
    let aktiv = true;
    let aktuelleUrl: string | null = null;
    (async () => {
      // Bis der neue Plan steht, kein Blattmaß des alten anzeigen.
      setBlatt(null);
      setPlanStand(null);
      // Piktogramme und Einweisungs-QR nebeneinander laden; der QR ist null,
      // wenn kein Link aktiv ist (dann kein Block auf dem Plan).
      const [bilder, qr] = await Promise.all([
        ladePiktogrammBilder(bloecke),
        sigeProject.project_id
          ? ladeEinweisungQrFuerProjekt(sigeProject.project_id, window.location.origin, window.location.pathname)
          : Promise.resolve(null),
      ]);
      const keys = piktoKeysFuer(bloecke);
      const geladen = keys.filter(k => bilder[k]).length;
      // Nur melden, wenn wirklich etwas fehlt — im Normalfall bleibt die Kopfzeile ruhig.
      // Die technische Quellenprüfung wandert in die Konsole statt in den Nutzerdialog.
      if (geladen < keys.length) {
        const fehlend = keys.length - geladen;
        if (aktiv) {
          setDiag(
            `${fehlend} von ${keys.length} Piktogrammen konnten nicht geladen werden — sie fehlen im PDF.`
          );
        }
        const fehlKey = keys.find(k => !bilder[k]);
        const iso = fehlKey ? aufloesePikto(fehlKey)?.iso : undefined;
        if (iso) console.warn(`Piktogramm-Quelle ${iso}: ${await pruefePiktoQuelle(iso)}`);
      } else if (aktiv) {
        setDiag('');
      }
      // Firmenvorgaben (Breite, Schrift) nur für den Druck einsetzen — der
      // Plan selbst bleibt ohne eigenen Wert und folgt späteren Änderungen.
      const druck = projektFuerDruck(sigeProject, vorlagen);
      const plan = erzeugeSigePlan(druck, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, bilder, qr, fusszeile);
      const url = URL.createObjectURL(plan.blob);
      aktuelleUrl = url;
      if (aktiv) {
        setBlobUrl(url);
        // Die Kennung entsteht aus denselben Eingaben wie das Blatt — wer
        // denselben Stand noch einmal ablegt, bekommt „schon abgelegt".
        setPlanStand({
          blob: plan.blob,
          druck,
          // GENAU die Eingaben von `erzeugeSigePlan` — auch Piktogramme, QR und
          // Fußzeile — dieselbe Rechnung wie im Export (usePdfExport.ts).
          fassungRef: sigePlanFassungRef(
            sigeProject.id,
            sigePlanInhaltsStand({
              druck, arbeitsschritte, gefaehrdungen, massnahmen, bloecke,
              piktoBilder: bilder, einweisungQr: qr, fusszeile,
            }),
          ),
          blatt: { breiteMm: plan.breiteMm, hoeheMm: plan.hoeheMm },
        });
        setBlatt({ breiteMm: plan.breiteMm, hoeheMm: plan.hoeheMm, gekappt: plan.gekappt });
      } else URL.revokeObjectURL(url);
    })();
    return () => { aktiv = false; if (aktuelleUrl) URL.revokeObjectURL(aktuelleUrl); };
  }, [sigeProject, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, fusszeile, vorlagen]);

  const schliessePlotter = useCallback(() => setPlotter(null), []);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }
    exportPdf(sigeProject, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, fusszeile);
  };

  /**
   * Nur App: den angezeigten Plan in den Teilen-Dialog — Knopf der
   * Seitenvorschau, wenn sich die Seiten nicht darstellen lassen. Dateiname
   * wie beim Export (hooks/usePdfExport.ts). Abgewartet und gesperrt: ein
   * zweites Tippen liefe sonst ins „Teilen läuft schon".
   */
  const handleTeilen = async () => {
    if (!blobUrl) return;
    setTeilenLaeuft(true);
    try {
      await dateiAusgeben(await blobFuerUrl(blobUrl), sigePlanDateiname(sigeProject));
    } catch (e) {
      ausgabeFehlerMelden(e);
    } finally {
      setTeilenLaeuft(false);
    }
  };

  // Der Ablagestand gilt nur für das Blatt, das gerade zu sehen ist: nach
  // einer Änderung am Plan (anderer Inhaltsstand) wäre „abgelegt" eine
  // Aussage über ein anderes Blatt.
  const ablageStand = onAblegen && ablage && planStand && ablage.fassungRef === planStand.fassungRef
    ? ablage.stand
    : null;
  const ablageLaeuft = ablageStand?.art === 'laeuft';
  const ablageTon = ablageStandTon(ablageStand);

  return (
    <div data-tutorial="sigeplan.pdf-vorschau" className="fixed inset-0 z-[150] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4 lg:p-8">
      <div className="bg-white rounded-2xl w-full h-full max-w-6xl flex flex-col shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3 justify-between items-center bg-slate-50">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Plan-Vorschau</h2>
            <p className="text-xs text-slate-500">
              {blatt
                ? <>Blatt {fmtZahl(Math.round(blatt.breiteMm))} × {fmtZahl(Math.round(blatt.hoeheMm))} mm</>
                : 'Druckansicht wird erzeugt'}
            </p>
            {blatt?.gekappt && (
              <p className="text-[11px] text-red-700">
                Der Plan ist länger als 5 m und wird unten abgeschnitten — Schrift verkleinern oder breiteres Papier wählen.
              </p>
            )}
            {diag && <p className="text-[11px] text-amber-700">{diag}</p>}
            {schriftFehler && <p className="text-[11px] text-red-700">{schriftFehler}</p>}
            {ablageStand && ablageStand.art !== 'laeuft' && (
              <p className={`text-[11px] flex items-center gap-1 ${ABLAGE_TON_KLASSE[ablageTon]}`}>
                {ablageTon === 'erfolg'
                  ? <CheckCircle2 className="w-3 h-3 shrink-0" />
                  : <AlertTriangle className="w-3 h-3 shrink-0" />}
                <span>{ablageStandText(ablageStand)}</span>
                {ablageStand.art === 'fehler' && planStand && onAblegen && (
                  // Wiederholt den festgehaltenen Auftrag (Anlass inklusive),
                  // nicht einen neuen nach dem Plan-Status.
                  <button type="button" onClick={() => (onErneutAblegen || onAblegen)(planStand)} className="underline font-bold">
                    Erneut ablegen
                  </button>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
             {onSchriftAendern && (
               <label
                 className="flex items-center gap-2 text-xs text-slate-600 mr-2"
                 title="Grundschrift der Tabelle — alle Schriften des Plans wachsen mit. Größere Schrift macht das Blatt länger."
               >
                 <span className="font-medium">Schrift</span>
                 <input
                   type="range"
                   min={PLAN_SCHRIFT.min}
                   max={PLAN_SCHRIFT.max}
                   step={PLAN_SCHRIFT.schritt}
                   value={schriftPt}
                   onChange={e => { const pt = Number(e.target.value); reglerRef.current = pt; setSchriftPt(pt); }}
                   aria-label="Schriftgröße des Plans"
                   aria-valuetext={`${fmtZahl(schriftPt)} pt`}
                   className="w-28 h-11 accent-blue-600"
                 />
                 <span className="tabular-nums w-12 text-right">{fmtZahl(schriftPt)} pt</span>
               </label>
             )}
             {/* Ablegen bucht nichts und lädt nichts herunter — es legt das
                 angezeigte Blatt als eigene Fassung in die Projektablage. */}
             {onAblegen && (
               <Button
                 variant="secondary"
                 onClick={() => { if (planStand && !ablageLaeuft) onAblegen(planStand); }}
                 disabled={!planStand || ablageLaeuft}
                 title="Den angezeigten Plan als eigene Fassung in der Projektablage (Dokumente) ablegen"
               >
                 {ablageLaeuft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderUp className="w-4 h-4 mr-2" />}
                 {ablageLaeuft ? 'Wird abgelegt …' : sigePlanAblageKnopfText(sigeProject.status)}
               </Button>
             )}
             {/* Abgerechnet wird nach dem Ankommen des Auftrags (plotAbrechnungen.ts), nicht beim Öffnen des Dialogs. */}
             <Button
               variant="secondary"
               onClick={() => { if (planStand && blatt) setPlotter({ stand: planStand, blatt }); }}
               disabled={!planStand || !blatt}
             >
               <Printer className="w-4 h-4 mr-2" />
               An Plotter senden
             </Button>
             <Button data-tutorial="sigeplan.pdf-herunterladen" variant="primary" onClick={handleDownload}>
               <Download className="w-4 h-4 mr-2" />
               Als PDF Herunterladen
             </Button>
             <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors">
               <X className="w-5 h-5" />
             </button>
          </div>
        </div>

        <div className="flex-1 bg-slate-200 p-4 relative">
           {!blobUrl ? (
              <div className="absolute inset-0 flex items-center justify-center">
                 <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
           ) : nativ ? (
              <NativePdfSeiten key={blobUrl} pdfUrl={blobUrl} onTeilen={handleTeilen} teilenLaeuft={teilenLaeuft} />
           ) : (
              <iframe 
                src={`${blobUrl}#toolbar=0`} 
                className="w-full h-full rounded shadow-sm border border-slate-300"
                title="PDF Vorschau"
              />
           )}
        </div>
      </div>
      {plotter && (
        <PlotterDialog
          blob={plotter.stand.blob}
          blatt={plotter.blatt}
          // Schlüssel = ID des Plans, Titel und Dateiname wie bisher: offene
          // Sendeabsichten (`senden:<sigeProjektId>`) und die Liste bleiben dieselben.
          dokument={plotDokumentSigePlan(sigeProject, fusszeile)}
          onClose={schliessePlotter}
          onNeuerAuftrag={plotAbrechnungProjektId ? id => merkePlotAuftrag(id, plotAbrechnungProjektId, 'sigeplan', plotter.stand) : undefined}
          // Immer melden, auch im Assistenten: ob abgerechnet wird, hat der
          // anlegende Dialog entschieden (gemerkt oder nicht).
          onGesendet={meldePlotAngekommen}
        />
      )}
    </div>
  );
};

/**
 * Vorschau mit den Plotter-Vorlagen der Firma.
 *
 * `key` an der Schriftvorgabe: kommen die Vorlagen erst an, während die
 * Vorschau schon offen ist (Laden nach dem Login läuft im Hintergrund),
 * beginnt das Fenster neu auf dem dann wirksamen Wert. Ohne Neuaufbau stünde
 * der Regler auf der alten Vorgabe, und der Entprell-Effekt hielte sie für
 * eine Eingabe und speicherte sie als eigenen Wert des Plans. Ein noch nicht
 * gespeicherter Reglerzug geht dabei nicht verloren — das Schließen sichert
 * ihn (siehe oben).
 */
export const PlanVorschau: React.FC<PlanVorschauProps> = (props) => {
  const vorlagen = useSigePlanVorlagen();
  return <PlanVorschauFenster key={vorlagen.standardSchriftPt} {...props} vorlagen={vorlagen} />;
};
